import { TFunction } from 'i18next';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import { FieldItem } from '@lib/interfaces/form';

type PermissionFieldNames = {
  titleName: string;
  allowDownloadName: string;
  allowPrintName: string;
};

type Args = {
  t: TFunction<'translation', undefined>;
  names: PermissionFieldNames;
  // Plan-gated feature — renders the lock chip instead of the checkbox.
  isPlanLocked?: boolean;
  // Extra config merged into the "Allow printing" field, which closes the
  // block and so carries the "custom permissions" message on the document
  // form. The category form has nothing to diverge from and omits it.
  allowPrintFieldProps?: Partial<FieldItem>;
};

// "Document permissions" block shared by the Document Category form (where the
// checkboxes define the category defaults) and the Document/import/bulk-edit
// forms (where they are prefilled from the selected category).
export default function getDocumentPermissionFields({
  t,
  names,
  isPlanLocked,
  allowPrintFieldProps,
}: Args): FieldItem[] {
  const checkBoxComponentProps = {
    options: [{ label: 'checked', value: 'checked' }],
    disabled: isPlanLocked,
    lockedProps: {
      locked: isPlanLocked,
      tooltip: t('upgrade-to-unlock-this-feature'),
    },
  };

  return [
    {
      name: names.titleName,
      customRender: () => (
        <FieldTitleRender
          title={t('document-permissions')}
          tooltipText={t('document-permissions-tooltip')}
          variant="body"
          strong
        />
      ),
      hideErrorsSpace: true,
    },
    {
      name: names.allowDownloadName,
      label: t('allow-download'),
      element: CheckBoxSingleBool,
      componentProps: checkBoxComponentProps,
      hideErrorsSpace: true,
    },
    {
      name: names.allowPrintName,
      label: t('allow-printing'),
      element: CheckBoxSingleBool,
      componentProps: checkBoxComponentProps,
      hideErrorsSpace: true,
      ...allowPrintFieldProps,
    },
  ];
}
